import { cloneElement, forwardRef, type JSXElementConstructor, type ReactElement, type Ref } from 'react';

import clsx from 'clsx';

import { Icon } from '@/components/icons/Icon';

interface IActionIcon {
  ariaLabel: string;
  isLoading?: boolean;
  onClick?: () => void;
  additionalClassNames?: string;
  children: React.ReactNode;
  dataTestId?: string;
  component?: 'button' | 'a';
  variant?: keyof typeof variants;
  sizeVariant?: keyof typeof sizeVariants;
  disabled?: boolean;
  href?: string;
}

const defaultActionIconStyling = 'hover:opacity-80 p-2 rounded-md disabled:cursor-not-allowed';

export const sizeVariants = {
  small: '20',
  large: '36',
};

export const variants = {
  primary: 'text-black hover:bg-gray-50 disabled:bg-gray-200',
  danger: 'bg-red-500 text-white border-transparent disabled:bg-red-100 disabled:hover:bg-red-100 hover:bg-red-700',
  link: 'text-accent-blue hover:bg-none !p-0 focus:outline-none focus:ring focus:ring-1 focus:ring-accent-blue',
};

export const ActionIcon = forwardRef(
  (
    {
      isLoading,
      ariaLabel,
      onClick,
      additionalClassNames,
      children,
      dataTestId,
      variant = 'primary',
      sizeVariant = 'small',
      disabled = false,
    }: IActionIcon,
    ref: Ref<HTMLButtonElement & HTMLAnchorElement> | React.ForwardedRef<HTMLButtonElement>,
  ) => (
    <button
      data-testid={dataTestId}
      aria-label={ariaLabel}
      className={clsx(defaultActionIconStyling, variants[variant], additionalClassNames)}
      disabled={disabled}
      ref={ref}
      onClick={onClick}
    >
      {isLoading ? (
        <Icon
          icon="spinner"
          iconClass={`${
            variant === 'primary'
              ? disabled
                ? 'stroke-gray-800'
                : 'stroke-black'
              : variant === 'danger'
              ? 'stroke-white'
              : ''
          }`}
        />
      ) : (
        children &&
        cloneElement(
          children as ReactElement<
            { size: string; stroke: number; className: string },
            string | JSXElementConstructor<unknown>
          >,
          {
            className: disabled ? 'text-gray-500' : '',
            size: sizeVariants[sizeVariant],
            stroke: 2,
          },
        )
      )}
    </button>
  ),
);

ActionIcon.displayName = 'ActionIcon';
